/**
 * OJ Product carousel.
 *
 * Three jobs, all of them small: crossfade the hero video in and out,
 * keep the price in step with the variant picker, and drive the desktop
 * arrows. Add to cart is not handled here - the card posts through the
 * theme's own <product-form>, so the cart drawer behaves exactly as it
 * does everywhere else.
 */
if (!customElements.get('oj-product-carousel')) {
  class OjProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.js-oj-pc-track');
      if (!this.track) return;

      this.cards = Array.from(this.querySelectorAll('.js-oj-pc-card'));
      this.mobileVideo = this.dataset.mobileVideo || 'autoplay';

      // Hover exists or it does not - the two paths are genuinely
      // different behaviours, not one behaviour with a breakpoint.
      this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.initTabs();
      this.initVideos();
      this.initVariantPickers();
      this.initArrows();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
    }

    /* ---------------------------------------------------------------- */

    initTabs() {
      this.tabs = Array.from(this.querySelectorAll('.js-oj-pc-tab'));
      if (!this.tabs.length) return;

      this.heading = this.querySelector('.js-oj-pc-heading');
      this.links = Array.from(this.querySelectorAll('.js-oj-pc-link'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.tab));
      });

      // Left/right walks the tabs, which is what a tablist is expected to
      // do - without it the only way across is one Tab press per button.
      this.addEventListener('keydown', (evt) => {
        if (!evt.target.classList.contains('js-oj-pc-tab')) return;
        const delta = evt.key === 'ArrowRight' ? 1 : evt.key === 'ArrowLeft' ? -1 : 0;
        if (!delta) return;
        evt.preventDefault();
        const i = (this.tabs.indexOf(evt.target) + delta + this.tabs.length) % this.tabs.length;
        this.selectTab(this.tabs[i].dataset.tab);
        this.tabs[i].focus();
      });
    }

    selectTab(name) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.tab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.cards.forEach((card) => {
        const active = card.dataset.tab === name;
        card.hidden = !active;
        // A card scrolled out of the way while it was visible would keep
        // its video running behind a hidden panel otherwise.
        if (!active) this.stop(card);
      });

      if (this.heading) {
        // getAttribute, not dataset: the camelCase mapping only fires on a
        // hyphen followed by a letter, so data-heading-2 stays
        // dataset['heading-2'] and dataset.heading2 is undefined.
        const next = this.heading.getAttribute('data-heading-' + name);
        if (next) this.heading.textContent = next;
      }

      this.links.forEach((link) => {
        link.hidden = link.dataset.tab !== name;
      });

      // Back to the first card, so switching tab does not land you in the
      // middle of a set you have not seen the start of.
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.updateArrows();
    }

    /* ---------------------------------------------------------------- */

    initVideos() {
      const cardsWithVideo = this.cards.filter((card) => card.querySelector('.js-oj-pc-video'));
      if (!cardsWithVideo.length) return;

      if (this.canHover) {
        cardsWithVideo.forEach((card) => {
          card.addEventListener('mouseenter', () => this.play(card));
          card.addEventListener('mouseleave', () => this.stop(card));
          // Keyboard focus moving into the card counts as hover: the
          // link inside it is focusable, so tabbing through should show
          // the same thing a pointer would.
          card.addEventListener('focusin', () => this.play(card));
          card.addEventListener('focusout', () => this.stop(card));
        });
        return;
      }

      if (this.mobileVideo === 'off') return;

      if (this.mobileVideo === 'tap') {
        cardsWithVideo.forEach((card) => {
          const media = card.querySelector('.oj-pc__media');
          if (!media) return;
          // First tap plays instead of following the link, so the video
          // is reachable at all on touch; the second tap navigates.
          media.addEventListener('click', (evt) => {
            if (card.classList.contains('is-playing')) return;
            evt.preventDefault();
            this.play(card);
          });
        });
        return;
      }

      // autoplay: muted, in view, and paused again on the way out so a
      // long carousel is not decoding half a dozen videos at once.
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.play(entry.target);
            } else {
              this.stop(entry.target);
            }
          });
        },
        { threshold: 0.6 }
      );

      cardsWithVideo.forEach((card) => this.observer.observe(card));
    }

    play(card) {
      const video = card.querySelector('.js-oj-pc-video');
      if (!video) return;

      card.classList.add('is-playing');
      // play() rejects on its own when the browser blocks it (a data
      // saver, a background tab, reduced motion on iOS). The still is
      // still underneath, so there is nothing to recover from.
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(() => card.classList.remove('is-playing'));
      }
    }

    stop(card) {
      const video = card.querySelector('.js-oj-pc-video');
      if (!video) return;

      card.classList.remove('is-playing');
      video.pause();
      // Back to the first frame, so the next hover starts the clip from
      // the top rather than resuming mid-shot.
      video.currentTime = 0;
    }

    /* ---------------------------------------------------------------- */

    initVariantPickers() {
      this.querySelectorAll('.js-oj-pc-variant').forEach((select) => {
        select.addEventListener('change', () => {
          const card = select.closest('.js-oj-pc-card');
          const price = card && card.querySelector('.js-oj-pc-price');
          const button = card && card.querySelector('.oj-pc__add');
          const option = select.options[select.selectedIndex];
          if (!option) return;

          if (price && option.dataset.price) price.textContent = option.dataset.price;

          // The struck price belongs to the variant, not the product: a
          // kit size can be reduced while another is not, and a stale
          // "was" beside a new price is worse than none.
          const was = card && card.querySelector('.js-oj-pc-was');
          if (was) {
            const compare = option.dataset.compare || '';
            was.textContent = compare;
            was.hidden = compare === '';
          }
          // A sold-out variant is rendered disabled, so it cannot be
          // reached through the select - but a theme editor change can
          // leave one selected, and the button should follow.
          if (button) button.disabled = option.disabled;
        });
      });
    }

    /* ---------------------------------------------------------------- */

    initArrows() {
      this.prev = this.querySelector('.js-oj-pc-prev');
      this.next = this.querySelector('.js-oj-pc-next');
      if (!this.prev && !this.next) return;

      const step = () => {
        // First card still on screen: with tabs on, this.cards[0] can be
        // hidden, and a hidden element measures zero.
        const card = this.cards.find((c) => !c.hidden);
        if (!card) return this.track.clientWidth;
        // One card plus the gap, so a click lands the next card exactly
        // where the last one started rather than drifting out of step.
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };

      if (this.prev) {
        this.prev.addEventListener('click', () => {
          this.track.scrollBy({ left: -step() * this.dir(), behavior: 'smooth' });
        });
      }

      if (this.next) {
        this.next.addEventListener('click', () => {
          this.track.scrollBy({ left: step() * this.dir(), behavior: 'smooth' });
        });
      }

      this.onScroll = this.updateArrows.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.updateArrows();
    }

    /**
     * Right to left flips the sign of scrollLeft, so scrollBy has to be
     * flipped with it or the arrows would each scroll the wrong way.
     */
    dir() {
      return getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    }

    updateArrows() {
      if (!this.prev && !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = Math.abs(this.track.scrollLeft);
      // A pixel of slack: sub-pixel widths mean the end of the scroll
      // rarely lands on an exact integer.
      if (this.prev) this.prev.hidden = pos <= 1;
      if (this.next) this.next.hidden = pos >= max - 1;
    }
  }

  customElements.define('oj-product-carousel', OjProductCarousel);
}
